import { useCallback, useState } from 'react';
import { useRecaptcha } from './useRecaptcha.js';
import { useSubmission } from './useSubmission.js';
import {
  buildInitialFormData,
  shouldShowAddressAfterCharge,
  shouldShowAddressToBoleto,
  shouldShowUpsell
} from '../flow/steps.js';
import { trackBeginCheckout } from '../utils/tracking.js';

/**
 * The engine both roots share: form state, the step machine and the two calls
 * that leave the browser. A root supplies its own step list and renders its own
 * shell around it — what happens between donorInfo and confirmation is the same
 * either way.
 *
 * Steps that don't apply are skipped during navigation instead of being removed
 * from the list, so indexes stay stable while the donor fills the form.
 */
export function useAcquisitionFlow({ config, steps, formDataOverrides = null, isPreview = false }) {
  const createFormData = () => buildInitialFormData(config, formDataOverrides);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState(createFormData);

  const captchaRequired = Boolean(config.CaptchaRequired && config.CaptchaSiteKey);
  const { getToken } = useRecaptcha({
    siteKey: config.CaptchaSiteKey || null,
    enabled: captchaRequired
  });

  const currentStep = steps[currentStepIndex];

  const handleChange = useCallback((updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  }, []);

  function goToStep(stepKey) {
    const index = steps.indexOf(stepKey);
    if (index >= 0) setCurrentStepIndex(index);
  }

  function handleNext() {
    if (!isPreview && (currentStep === 'amount' || currentStep === 'cart')) {
      trackBeginCheckout(config, formData);
    }

    let nextIndex = currentStepIndex + 1;

    if (steps[nextIndex] === 'upsell') {
      setFormData(prev => ({ ...prev, upsellAccepted: null }));
    }

    while (steps[nextIndex] === 'upsell' && !shouldShowUpsell(config, formData)) {
      nextIndex += 1;
    }

    while (steps[nextIndex] === 'addressToBoleto' && !shouldShowAddressToBoleto(formData)) {
      nextIndex += 1;
    }

    while (steps[nextIndex] === 'addressAfterCharge' && !shouldShowAddressAfterCharge(config, formData)) {
      nextIndex += 1;
    }

    if (nextIndex < steps.length) {
      setCurrentStepIndex(nextIndex);
    }
  }

  function handleBack() {
    let prevIndex = currentStepIndex - 1;

    // Skip back over steps that don't apply to the current flow.
    while (prevIndex >= 0 && (
      steps[prevIndex] === 'upsell' ||
      (steps[prevIndex] === 'addressToBoleto' && !shouldShowAddressToBoleto(formData)) ||
      (steps[prevIndex] === 'addressAfterCharge' && !shouldShowAddressAfterCharge(config, formData))
    )) {
      prevIndex -= 1;
    }

    if (prevIndex >= 0) {
      setCurrentStepIndex(prevIndex);
    }
  }

  const { isSubmitting, submitDonor, submitDonation } = useSubmission({
    config,
    formData,
    setFormData,
    captchaRequired,
    getToken,
    onAdvance: handleNext
  });

  async function handlePaymentSubmit() {
    if (shouldShowAddressToBoleto(formData)) {
      handleNext();
      return;
    }

    await submitDonation();
  }

  // A failed payment is recoverable: send the donor back to the payment step so they
  // can try another card or switch the payment method. The failed chargeResult is kept
  // (not cleared here) so submitDonation sends the new attempt through the update
  // endpoint; it is cleared right after the resubmission.
  function handleRetryPayment() {
    setFormData(prev => ({ ...prev, apiError: null }));
    goToStep('payment');
  }

  async function handleAddressSubmit() {
    const donorSaved = await submitDonor({ advance: false });
    if (!donorSaved) return;
    if (config.Behaviour.Goal === 'Lead') {
      handleNext();
      return;
    }

    await submitDonation();
  }

  function reset() {
    setCurrentStepIndex(0);
    setFormData(createFormData());
  }

  return {
    formData,
    setFormData,
    handleChange,
    steps,
    currentStep,
    currentStepIndex,
    goToStep,
    handleNext,
    handleBack,
    reset,
    isSubmitting,
    submitDonor,
    submitDonation,
    handlePaymentSubmit,
    handleAddressSubmit,
    handleRetryPayment
  };
}
